// ============================================================
// MyGene — themeUtils.ts  (updated)
// Integrates ColorGroups + DesignTokens into Ant Design's
// ConfigProvider and injects CSS custom properties for the
// complete surface / sidebar / typography token set.
// ============================================================

import { ThemeConfig, theme } from 'antd';
import { getColorGroup, applyCSSColorGroup, ColorGroup } from '../../design-system/colorGroups';
import { ANT_COMPONENT_TOKENS, TYPOGRAPHY, RADIUS } from '../../design-system/tokens';

// ── Surface token maps ───────────────────────────────────────

const DARK_SURFACE = {
	'--mg-bg': '#0a0a0a',
	'--mg-bg-subtle': '#111111',
	'--mg-bg-elevated': '#141414',
	'--mg-bg-card': '#161616',
	'--mg-border': 'rgba(255,255,255,0.08)',
	'--mg-border-strong': 'rgba(255,255,255,0.16)',
	'--mg-text': 'rgba(255,255,255,0.92)',
	'--mg-text-secondary': 'rgba(255,255,255,0.50)',
	'--mg-text-disabled': 'rgba(255,255,255,0.28)',
	'--mg-sidebar-bg': '#0d0d0d',
	'--mg-sidebar-border': 'rgba(255,255,255,0.06)',
};

const LIGHT_SURFACE = {
	'--mg-bg': '#f5f5f5',
	'--mg-bg-subtle': '#f0f0f0',
	'--mg-bg-elevated': '#ffffff',
	'--mg-bg-card': '#ffffff',
	'--mg-border': 'rgba(0,0,0,0.08)',
	'--mg-border-strong': 'rgba(0,0,0,0.16)',
	'--mg-text': 'rgba(0,0,0,0.88)',
	'--mg-text-secondary': 'rgba(0,0,0,0.48)',
	'--mg-text-disabled': 'rgba(0,0,0,0.28)',
	'--mg-sidebar-bg': '#ffffff',
	'--mg-sidebar-border': 'rgba(0,0,0,0.08)',
};

// ── CSS class helpers ────────────────────────────────────────

export function applyThemeClasses(mode: 'light' | 'dark'): void {
	const root = document.documentElement;
	if (mode === 'dark') {
		root.classList.add('dark-theme');
		root.classList.remove('light-theme');
		root.style.colorScheme = 'dark';
	} else {
		root.classList.add('light-theme');
		root.classList.remove('dark-theme');
		root.style.colorScheme = 'light';
	}
	// meta theme-color
	let meta = document.querySelector('meta[name="theme-color"]');
	if (!meta) {
		meta = document.createElement('meta');
		meta.setAttribute('name', 'theme-color');
		document.head.appendChild(meta);
	}
	meta.setAttribute('content', mode === 'dark' ? '#0a0a0a' : '#ffffff');
}

function injectSurfaceVars(mode: 'light' | 'dark'): void {
	const map = mode === 'dark' ? DARK_SURFACE : LIGHT_SURFACE;
	const root = document.documentElement;
	Object.entries(map).forEach(([k, v]) => root.style.setProperty(k, v));
}

// ── Ant Design theme builder ─────────────────────────────────

export function generateAntTheme(
	mode: 'light' | 'dark',
	accentKey: string,
): ThemeConfig {
	const group: ColorGroup = getColorGroup(accentKey);

	return {
		token: {
			// Brand
			colorPrimary: group.primary,
			colorSuccess: group.success,
			colorWarning: group.warning,
			colorError: group.danger,
			colorInfo: group.info,

			// Surface (dark/light)
			colorBgBase: mode === 'dark' ? '#0a0a0a' : '#ffffff',
			colorTextBase: mode === 'dark' ? '#ffffff' : '#000000',
			colorBgContainer: mode === 'dark' ? '#161616' : '#ffffff',
			colorBgElevated: mode === 'dark' ? '#1e1e1e' : '#ffffff',
			colorBgLayout: mode === 'dark' ? '#0a0a0a' : '#f5f5f5',
			colorBorder: mode === 'dark'
				? 'rgba(255,255,255,0.10)'
				: 'rgba(0,0,0,0.10)',
			colorBorderSecondary: mode === 'dark'
				? 'rgba(255,255,255,0.06)'
				: 'rgba(0,0,0,0.06)',

			// Typography — compact scale, 12px base
			fontFamily: TYPOGRAPHY.fontFamily,
			fontSize: TYPOGRAPHY.size.base,      // 12
			fontSizeSM: TYPOGRAPHY.size.xs,      // 11
			fontSizeLG: TYPOGRAPHY.size.md,      // 14
			fontSizeXL: TYPOGRAPHY.size.lg,      // 16

			// Shape — tighter radii for clinical precision
			borderRadius: RADIUS.sm,    // 4
			borderRadiusSM: RADIUS.xs,  // 3
			borderRadiusLG: RADIUS.md,  // 6
			borderRadiusXS: 2,

			// Motion
			motionDurationFast: '120ms',
			motionDurationMid: '200ms',
			motionDurationSlow: '350ms',
			motionEaseInOut: 'cubic-bezier(0.16, 1, 0.3, 1)',

			// Controls — compact 32px (was 34px)
			controlHeight: 32,
			controlHeightSM: 24,
			controlHeightLG: 40,

			// Box shadows
			boxShadow: mode === 'dark'
				? '0 4px 16px rgba(0,0,0,0.5)'
				: '0 4px 16px rgba(0,0,0,0.10)',
			boxShadowSecondary: mode === 'dark'
				? '0 2px 8px rgba(0,0,0,0.45)'
				: '0 2px 8px rgba(0,0,0,0.08)',
		},

		algorithm: mode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,

		components: ANT_COMPONENT_TOKENS as any,
	};
}

// ── Master "apply full theme" ────────────────────────────────

export async function applyFullTheme(
	mode: 'light' | 'dark',
	accentKey: string,
): Promise<ThemeConfig> {
	try {
		applyThemeClasses(mode);
		injectSurfaceVars(mode);
		const group = getColorGroup(accentKey);
		applyCSSColorGroup(group);
		return generateAntTheme(mode, accentKey);
	} catch {
		applyThemeClasses('dark');
		injectSurfaceVars('dark');
		applyCSSColorGroup(getColorGroup('blue'));
		return generateAntTheme('dark', 'blue');
	}
}

/** @deprecated */
export function loadThemeCSS(): Promise<void> { return Promise.resolve(); }
/** @deprecated */
export function getPrimeReactTheme(): string { return ''; }